import { clientStore } from 'apps/client/app';
import { frameStore } from 'apps/components/Frame/appManager';
import { communityRoutes } from 'apps/components/Frame/config';
import { SpaceModel } from 'apps/space/app';
import { goBack, routerStore } from 'core/routing';
import { isDesktopCompatibility } from 'hooks/useDesktopCompatible';
import {
  isInDesktopMode,
  openUrlInBrowser,
} from 'libs/gutClient/electronBridge';
import { isProfileSlug } from 'utils';

export const routesWithoutAddButton = [
  'workspace.upgrade',
  'workspace.upgradePlan',
  'home.newInitiative',
  'onboarding.choosePlan',
  'onboarding.subscription.company',
  'onboarding.subscription.individual',
];

export const routesWithoutNotification = [
  'workspace.upgrade',
  'workspace.upgradePlan',
  'home.newInitiative',
  'home.notifications',
  'onboarding.choosePlan',
  'onboarding.subscription.company',
  'onboarding.subscription.individual',
];

export const routesWithoutInbox = [
  'workspace.upgrade',
  'workspace.upgradePlan',
  'home.newInitiative',
  'onboarding.choosePlan',
  'onboarding.subscription.company',
  'onboarding.subscription.individual',
];

export const communityLandingPages = [
  'home.community.featured',
  'home.community.topics',
  'home.community.projects',
  'home.community.tasks',
  'home.community.members',
];

const routesWithoutFrame = [
  'home.signIn',
  'home.signUp',
  'home.newProject',
  'viewer',
  'onboarding.confirmProfile',
  'onboarding.checkEmail',
];

export const homeRFQsQuotesOrdersRoutes = [
  'workspace.quotes',
  'workspace.orders',
  'workspace.rfqs',
  'workspace.intermediator.rfqs',
  'workspace.intermediator.quotes',
  'workspace.intermediator.orders',
];

export const showIntermediatorSwitcherRoutes = [
  ...homeRFQsQuotesOrdersRoutes,
  'intermediator.order',
  'intermediator.rfq',
  'intermediator.quote',
  'order',
  'rfq',
  'quote',
  'quotes',
];

export const RFQQuoteOrderRoutes = ['rfq', 'quote', 'order'];

export const intermediatorRFQQuoteOrderRoutes = RFQQuoteOrderRoutes.map(
  name => `intermediator.${name}`,
);

export const manufacturerRFQQuoteOrderRoutes = RFQQuoteOrderRoutes.map(
  name => `manufacturer.${name}`,
);

export const RFQQuoteOrderDetailRoutes = [
  ...RFQQuoteOrderRoutes,
  ...intermediatorRFQQuoteOrderRoutes,
  ...manufacturerRFQQuoteOrderRoutes,
];

const projectRoutes = [
  'project',
  'project.version',
  'project.files',
  'project.files.directory',
  'project.file',
  'project.contribution',
  'project.conflicts',
  'project.issues',
  'project.issue',
  'project.settings',
  'project.settings.teams',
  'project.settings.general',
  'project.settings.admin',
];

export const profileProjectRoutes = projectRoutes.map(
  name => `profile.${name}`,
);

export const initiativeRoutes = projectRoutes.map(name => `initiative.${name}`);

export const spaceProjectRoutes = [
  ...profileProjectRoutes,
  ...initiativeRoutes,
];

export const spaceFileAndProjects = [
  'profile.shared.file',
  'initiative.shared.file',
  ...spaceProjectRoutes,
];

export const needAddWorkspaceRoutes = [
  ...RFQQuoteOrderDetailRoutes,
  ...spaceFileAndProjects,
  'profile',
  'initiative',
];

export const sharedWithMeRoutes = [
  'workspace.sharedWithFiles',
  'workspace.sharedWithProjects',
];

export const routesWithViewType = [
  'workspace.spaceProjects',
  'workspace.sharedWithProjects',
];

export const settingsRoutes = [
  'workspace.spaceSettings.teams',
  'workspace.spaceSettings.account',
  'workspace.spaceSettings.billing',
  'workspace.spaceSettings.notifications',
];

const showSidebarRoutes = [
  'workspace',
  ...homeRFQsQuotesOrdersRoutes,
  'workspace.spaceFiles',
  'workspace.spaceProjects',
  ...sharedWithMeRoutes,
  ...settingsRoutes,
];

const routesWithoutSubHeader = [
  ...routesWithoutFrame,
  'workspace.upgrade',
  'home.newInitiative',
  'workspace.upgradePlan',
  'workspace.upgrade',
];

const notGoConfirmRoutes = [
  'onboarding.checkEmail',
  'onboarding.confirmProfile',
];

export const RFQsQuotesOrdersRoutes = [
  'rfq',
  'workspace.rfqs',
  'workspace.intermediator.rfqs',
  'intermediator.rfq',
  'workspace.manufacturer.rfqs',
  'workspace.intermediator.manufacturer.rfqs',
  'manufacturer.rfq',
  // quotes
  'quote',
  'quotes',
  'workspace.quotes',
  'intermediator.quote',
  'workspace.intermediator.quotes',
  // orders
  'order',
  'workspace.orders',
  'intermediator.order',
  'workspace.intermediator.orders',
  'intermediator.order',
  'workspace.manufacturer.orders',
  'workspace.intermediator.manufacturer.orders',
  'manufacturer.order',
];

export const CADRoutes = [
  'rfq',
  'intermediator.rfq',
  'profile.shared.file',
  'initiative.shared.file',
];

export const routeMenuConfigs: { matchRoutes: string[]; label: string }[] = [
  {
    matchRoutes: ['workspace.spaceFiles'],
    label: 'Files',
  },
  {
    matchRoutes: settingsRoutes,
    label: 'Settings',
  },
  {
    matchRoutes: ['workspace.spaceProjects'],
    label: 'Projects',
  },
  {
    matchRoutes: sharedWithMeRoutes,
    label: 'Shared with me',
  },
  {
    matchRoutes: homeRFQsQuotesOrdersRoutes,
    label: 'RFQs, Quotes and Orders',
  },
];

export const needAddPrefixRoutes = [
  'workspace.spaceFiles',
  'workspace.spaceProjects',
  ...settingsRoutes,
  ...homeRFQsQuotesOrdersRoutes,
];

const routesWithShowProjectTag = [
  'home',
  'workspace',
  'workspace.spaceProjects',
  'workspace.sharedWithProjects',
];

export const getLabelByRouteName = (routeName: string) => {
  const matchItem = routeMenuConfigs.find(({ matchRoutes }) =>
    matchRoutes.some(matchRoute => matchRoute === routeName),
  );

  return matchItem?.label || '';
};

type RouteOrRouteName = string | { name: string };

const getRouteName = (routeOrRouteName: RouteOrRouteName) =>
  typeof routeOrRouteName === 'string'
    ? routeOrRouteName
    : routeOrRouteName.name;

export const isCommunityLandingPage = (routeOrRouteName: RouteOrRouteName) => {
  if (!routeOrRouteName) return false;
  const routeName = getRouteName(routeOrRouteName);
  if (!routeName) return false;
  return communityLandingPages.includes(routeName);
};

export const buildRouteFromSpace = (
  space: SpaceModel,
  preferWorkspaceLink: boolean,
) => {
  const spaceTypeLowerCased = space?.spaceType?.toLowerCase();

  let linkName = spaceTypeLowerCased;
  let linkParams: Object = {
    [spaceTypeLowerCased]: space.path,
  };

  const userHasAccessToWorkspace = clientStore.workspaces.some(
    workspace => workspace.id === space?.id,
  );

  if (preferWorkspaceLink && userHasAccessToWorkspace) {
    linkName = 'workspace';
    linkParams = { spacePath: space.path };
  }
  return { name: linkName, params: linkParams };
};

export const navigateToWorkSpace = async (route?: string) => {
  const { activeSpace } = clientStore;

  if (!activeSpace) {
    await clientStore.QueryAvailableSpaces(true);
  }

  // Some route cannot find slug, and reload page also exist no slug, so, need add option syntax
  const currentSlug = routerStore.route?.params?.spacePath || activeSpace?.path;

  if (currentSlug) {
    routerStore.navigate(route || 'workspace', {
      spacePath: currentSlug,
    });
  } else {
    routerStore.navigate('home');
  }
};

export const navigateToSettingsBilling = () => {
  navigateToWorkSpace('workspace.spaceSettings.billing');
};

export const isAdminForActiveSpace = (
  activeSpace = clientStore.activeSpace,
) => {
  if (!activeSpace) return false;

  const { content } = activeSpace;
  return !!content.canDelete;
};

export const isInitiativeSpace = (activeSpace = clientStore.activeSpace) => {
  return activeSpace?.spaceType === 'INITIATIVE';
};

export const handleCommunityClick = async () => {
  if (
    clientStore.atCommunityRoute &&
    !isCommunityLandingPage(routerStore.route) &&
    isCommunityLandingPage(routerStore.previousRoute)
  ) {
    goBack();
  } else {
    const compatibleWithTab = await isDesktopCompatibility({
      feature: 'tab',
    });

    if (isInDesktopMode() && compatibleWithTab) {
      openUrlInBrowser(
        `${window.location.origin}${routerStore.router.buildUrl(
          'home.community.featured',
        )}`,
      );
      return;
    }
    routerStore.navigate('home.community.featured');
  }
};

export const getSystemCondition = () => {
  const { route } = routerStore;

  const isLoggedIn = !!clientStore.signedInProfile;

  const isMakePartRoute = route.name === 'workspace.make-part.file';
  const { diff } = route.params || {};

  const shouldShowMakePartOperator = !isMakePartRoute;
  const { isMobile, profileResponse } = clientStore;

  const isProfileSpaceForCurrentRouteSlug = isProfileSlug(
    clientStore.slugFromRouteOrSpace,
  );

  const isActiveProfile = isProfileSlug(clientStore.activeSpace?.path);

  const { sharedFileManager, file, project } = frameStore;

  // Profile space always can operate
  const isEffectivePlan =
    clientStore.currentSpaceData?.hasProSubscription ||
    isProfileSpaceForCurrentRouteSlug;

  const shouldShowFrame =
    isLoggedIn && !routesWithoutFrame.includes(route.name);

  const shouldShowFrameSideBar =
    isLoggedIn && showSidebarRoutes.includes(route.name) && !isMobile;

  const shouldHideSubHeader =
    !isLoggedIn ||
    routesWithoutSubHeader.includes(route.name) ||
    route.name.includes('onboarding.') ||
    isMobile;

  const shouldShowIntermediatorSwitcher =
    isLoggedIn &&
    clientStore.hasRole('INTERMEDIATOR') &&
    showIntermediatorSwitcherRoutes.includes(route.name);

  const shouldShowIntermediatorDescription = route.name !== 'workspace';

  const revisionsConnection = sharedFileManager?.revisionsConn;
  const shouldShowRevisionsDropdown = revisionsConnection?.totalCount > 1;

  const shouldShowCADRevisions =
    (shouldShowRevisionsDropdown ||
      (sharedFileManager?.sharedFile?.canUpdate &&
        !sharedFileManager?.waitingForConversion)) &&
    shouldShowMakePartOperator;

  const { isRestricted } = sharedFileManager?.sharedFileQuery?.result || {};

  const isProjectUnrestricted = !project?.isRestricted && !isRestricted;
  const canDownload = file?.canDownload || project?.canDownload;

  const showSearchBar =
    clientStore.atCommunityRoute ||
    communityRoutes.includes(route.name) ||
    route.name === 'home.search';

  const shouldHideLeftMenusOnMobile =
    isMobile && route.name.includes('onboarding');

  const isDownloadAllowed =
    canDownload &&
    isLoggedIn &&
    route.name !== 'workspace.share-file' &&
    isProjectUnrestricted &&
    shouldShowMakePartOperator;

  const shouldShowCADDownloadBtn =
    file && !diff && (isDownloadAllowed || shouldShowIntermediatorSwitcher);

  const isInNotificationPage = route.name === 'home.notifications';

  const shouldShowAddButton = !routesWithoutAddButton.includes(route.name);

  const shouldShowNotification = !routesWithoutNotification.includes(
    route.name,
  );

  const shouldShowInbox = !routesWithoutInbox.includes(route.name);

  const showPublicOrPrivateForProjectTag =
    routesWithShowProjectTag.includes(route.name) && isLoggedIn;

  const shouldGotoConfirmProfilePage =
    profileResponse &&
    !profileResponse.profileConfirmed &&
    !notGoConfirmRoutes.includes(routerStore.route.name);

  return {
    isMobile,
    isLoggedIn,
    shouldHideLeftMenusOnMobile,
    showSearchBar,
    isMakePartRoute,
    shouldShowFrame,
    isActiveProfile,
    isEffectivePlan,
    shouldShowInbox,
    shouldShowAddButton,
    shouldHideSubHeader,
    isInNotificationPage,
    shouldShowNotification,
    shouldShowFrameSideBar,
    shouldShowCADRevisions,
    shouldShowCADDownloadBtn,
    shouldShowMakePartOperator,
    shouldGotoConfirmProfilePage,
    shouldShowIntermediatorSwitcher,
    showPublicOrPrivateForProjectTag,
    isProfileSpaceForCurrentRouteSlug,
    shouldShowIntermediatorDescription,
  };
};
